import { useState } from 'react';
import { Prompt, useNotifications } from '@weareopenr/component-library';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deactivateUser } from 'src/api/deactivateUser/deactivateUser';
import { IUser } from 'src/types';
import { useNavigate } from 'react-router-dom';
import { useUserTracking } from 'src/hooks';

export const useDeactivateUser = () => {
  const [userToDeactivate, setUserToDeactivate] = useState<IUser | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const createNotification = useNotifications();
  const { trackEvent, captureException } = useUserTracking();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!userToDeactivate) throw new Error('No user selected');
      return deactivateUser(userToDeactivate);
    },
    onSuccess: () => {
      if (userToDeactivate) {
        trackEvent('user_deactivated', { email: userToDeactivate.email });
      }
      createNotification({
        severity: 'success',
        message: 'User deactivated successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      navigate('/users');
    },
    onError: (e) => {
      createNotification({
        severity: 'error',
        message: 'Something went wrong whilst deactivating user',
      });

      captureException(e);
    },
    onSettled: () => {
      setShowPrompt(false);
      setUserToDeactivate(null);
    },
  });

  const openPrompt = (user: IUser) => {
    setUserToDeactivate(user);
    setShowPrompt(true);
  };

  const PromptComponent = userToDeactivate ? (
    <Prompt
      closePrompt={() => {
        setShowPrompt(false);
        setUserToDeactivate(null);
      }}
      open={showPrompt}
      buttonHandler={() => mutate()}
      buttonText="Deactivate"
      isSubmitting={isPending}
      promptHeading={`Are you sure you want to deactivate ${userToDeactivate.first_name} ${userToDeactivate.last_name}?`}
    >
      This user will no longer be able to access the system.
    </Prompt>
  ) : null;

  return { openPrompt, PromptComponent };
};
